
let sentryModulePromise = null

function loadSentryModule() {
    if (!sentryModulePromise) {
        sentryModulePromise = import(/* webpackChunkName: "analytics-sentry" */ '@sentry/browser')
            .catch((error) => {
                sentryModulePromise = null
                throw error
            })
    }

    return sentryModulePromise
}

class SentryClientManager {
    constructor() {
        this.initialized = false
        this.config = null
        this.sentry = null
        this.initializingPromise = null
        this.consentGranted = false
    }

    /**
     * Check if user has granted crash reporting consent
     * COPPA/GDPR-K Compliance - Finding 5.1
     * Sentry must not collect data without explicit consent
     * @param {Object} config - Configuration object
     * @returns {boolean}
     */
    checkCrashReportingConsent(config) {
        // Check for explicit consent flag from user preferences
        const userConsent = config?.userConsent || {}
        
        // Consent can come from:
        // 1. User's analytics/error reporting preferences
        // 2. Parent-granted consent for minor users
        // 3. Cookie consent for crash reporting
        
        if (userConsent.hasCrashReportingConsent === true) {
            return true
        }
        
        // For minor users, check if parent granted analytics consent
        if (userConsent.isMinor && !userConsent.parentalConsentForAnalytics) {
            console.log('[Sentry] Crash reporting disabled - minor user without parental consent')
            return false
        }
        
        // Check if user has general analytics consent which covers crash reporting
        if (userConsent.hasAnalyticsConsent === true) {
            return true
        }
        
        // Default: no consent, no tracking
        return false
    }

    /**
     * Initialize Sentry with configuration
     * COPPA/GDPR-K Compliance: Only initializes if consent is granted
     * @param {Object} config - Configuration object containing Sentry settings
     */
    async initialize(config) {
        if (this.initialized) {
            return
        }

        if (this.initializingPromise) {
            return this.initializingPromise
        }

        this.config = config
        const sentryConfig = config.analytics?.sentry

        if (!sentryConfig?.enabled || !sentryConfig.dsn) {
            return
        }

        // COPPA/GDPR-K Compliance: Check consent before loading Sentry
        if (!this.checkCrashReportingConsent(config)) {
            console.log('[Sentry] Crash reporting disabled - no consent granted')
            return
        }

        this.consentGranted = true

        this.initializingPromise = (async () => {
            try {
                const Sentry = await loadSentryModule()
                this.sentry = Sentry

                Sentry.init({
                    dsn: sentryConfig.dsn,
                    environment: sentryConfig.environment || 'production',
                    sampleRate: sentryConfig.sampleRate || 1.0,
                    tracesSampleRate: sentryConfig.tracesSampleRate || 0.1,
                    // COPPA/GDPR-K: Never send PII by default
                    sendDefaultPii: false,
                    integrations: [
                        Sentry.browserTracingIntegration(),
                        Sentry.replayIntegration({
                            maskAllText: true, // COPPA: Mask all text in replays
                            blockAllMedia: true, // COPPA: Block media in replays
                        }),
                        Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
                    ],
                    enableLogs: true,
                    // Configure replay sampling to avoid warnings
                    replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate || 0.1,
                    replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate || 1.0,
                    beforeSend(event) {
                        // COPPA/GDPR-K Compliance: Strip all PII from error reports
                        // Remove cookies, auth headers, and any user identifying info
                        if (event.request) {
                            delete event.request.cookies
                            delete event.request.headers?.authorization
                            delete event.request.headers?.cookie
                        }
                        
                        // Anonymize/remove user data
                        if (event.user) {
                            // Keep only anonymized session ID, remove everything else
                            const anonId = event.user.id
                            event.user = { id: anonId }
                        }
                        
                        // Remove IP address (Sentry may add this)
                        delete event.user?.ip_address
                        
                        // Filter out sensitive data from extra context
                        if (event.extra) {
                            delete event.extra.email
                            delete event.extra.username
                            delete event.extra.parentEmail
                        }
                        
                        // Filter out known non-critical errors
                        if (event.exception) {
                            const error = event.exception.values?.[0]
                            if (error?.type === 'ChunkLoadError' || 
                                error?.value?.includes('Loading chunk') ||
                                error?.value?.includes('NetworkError')) {
                                return null
                            }
                        }
                        
                        return event
                    },
                    beforeSendTransaction(event) {
                        // Filter out low-priority transactions
                        if (event.transaction === 'GET /' || event.transaction === 'pageload') {
                            return null
                        }
                        return event
                    }
                })

                // Set global tags
                Sentry.setTag('client.version', VERSION || 'unknown')
                Sentry.setTag('client.type', 'game')
                
                // Set user context with browser info
                Sentry.setUser({
                    id: this.generateSessionId(),
                    browser: navigator.userAgent,
                    language: navigator.language,
                    platform: navigator.platform
                })

                // Set additional context
                Sentry.setContext('browser', {
                    name: this.getBrowserName(),
                    version: this.getBrowserVersion(),
                    viewport: {
                        width: window.innerWidth,
                        height: window.innerHeight
                    },
                    screen: {
                        width: screen.width,
                        height: screen.height
                    }
                })

                this.initialized = true
            } catch (error) {
                this.sentry = null
                // Silent initialization failure
            }
        })()

        try {
            await this.initializingPromise
        } finally {
            this.initializingPromise = null
        }
    }

    /**
     * Generate a session ID for user tracking
     * @returns {string}
     */
    generateSessionId() {
        return 'client_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now()
    }

    /**
     * Get browser name
     * @returns {string}
     */
    getBrowserName() {
        const userAgent = navigator.userAgent
        if (userAgent.includes('Chrome')) return 'Chrome'
        if (userAgent.includes('Firefox')) return 'Firefox'
        if (userAgent.includes('Safari')) return 'Safari'
        if (userAgent.includes('Edge')) return 'Edge'
        return 'Unknown'
    }

    /**
     * Get browser version
     * @returns {string}
     */
    getBrowserVersion() {
        const userAgent = navigator.userAgent
        const match = userAgent.match(/(chrome|firefox|safari|edge)\/(\d+)/i)
        return match ? match[2] : 'Unknown'
    }

    /**
     * Capture an exception
     * @param {Error} error - The error to capture
     * @param {Object} context - Additional context
     */
    captureException(error, context = {}) {
        if (!this.initialized || !this.sentry) return

        this.sentry.withScope((scope) => {
            // Add context
            Object.keys(context).forEach(key => {
                scope.setContext(key, context[key])
            })

            this.sentry.captureException(error)
        })
    }

    /**
     * Capture a message
     * @param {string} message - The message to capture
     * @param {string} level - The severity level
     * @param {Object} context - Additional context
     */
    captureMessage(message, level = 'info', context = {}) {
        if (!this.initialized || !this.sentry) return

        this.sentry.withScope((scope) => {
            // Add context
            Object.keys(context).forEach(key => {
                scope.setContext(key, context[key])
            })

            this.sentry.captureMessage(message, level)
        })
    }

    /**
     * Set user context
     * @param {Object} user - User information
     */
    setUser(user) {
        if (!this.initialized || !this.sentry) return
        this.sentry.setUser(user)
    }

    /**
     * Set additional context
     * @param {string} key - Context key
     * @param {Object} context - Context data
     */
    setContext(key, context) {
        if (!this.initialized || !this.sentry) return
        this.sentry.setContext(key, context)
    }

    /**
     * Add breadcrumb
     * @param {Object} breadcrumb - Breadcrumb data
     */
    addBreadcrumb(breadcrumb) {
        if (!this.initialized || !this.sentry) return
        this.sentry.addBreadcrumb(breadcrumb)
    }

    /**
     * Start a span (replaces startTransaction in newer Sentry versions)
     * @param {Object} context - Span context
     * @returns {Object} Span object
     */
    startTransaction(context) {
        if (!this.initialized || !this.sentry) return null
        // Use startSpan instead of deprecated startTransaction
        return this.sentry.startSpan(context, (span) => span)
    }

    /**
     * Track game events
     * @param {string} eventName - Name of the event
     * @param {Object} data - Event data
     */
    trackGameEvent(eventName, data = {}) {
        if (!this.initialized) return

        this.addBreadcrumb({
            message: eventName,
            category: 'game',
            level: 'info',
            data: data
        })
    }

    /**
     * Track network events
     * @param {string} eventName - Name of the event
     * @param {Object} data - Event data
     */
    trackNetworkEvent(eventName, data = {}) {
        if (!this.initialized) return

        this.addBreadcrumb({
            message: eventName,
            category: 'network',
            level: 'info',
            data: data
        })
    }

    /**
     * Track user actions
     * @param {string} action - User action
     * @param {Object} data - Action data
     */
    trackUserAction(action, data = {}) {
        if (!this.initialized) return

        this.addBreadcrumb({
            message: action,
            category: 'user',
            level: 'info',
            data: data
        })
    }

    /**
     * Handle unhandled promise rejections
     */
    setupGlobalErrorHandlers() {
        if (!this.initialized) return

        // Handle unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            this.captureException(event.reason, {
                type: 'unhandled_promise_rejection',
                promise: true
            })
        })

        // Handle global errors
        window.addEventListener('error', (event) => {
            // Filter out browser extension errors
            if (event.message && event.message.includes('Extension context invalidated') ||
                event.message && event.message.includes('message port closed') ||
                event.filename && event.filename.includes('extension://')) {
                return // Ignore browser extension errors
            }

            this.captureException(event.error || new Error(event.message), {
                type: 'global_error',
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno
            })
        })
    }
}

// Create singleton instance
const sentryClient = new SentryClientManager()

export default sentryClient